import { useEffect, useRef, useState } from "react";
import {
  getAppleIdToken,
  isAppleConfigured,
  isGoogleConfigured,
  mountGoogleButton,
  unmountGoogleButton,
  type AppleCredential,
} from "../services/socialAuth.service.js";

/**
 * The "or continue with a provider" block shared by the Login and SignUp pages.
 *
 * It owns only presentation and the provider handshake; what a token MEANS is the caller's business
 * — Login exchanges it for a session, SignUp exchanges it for an account. That split is what lets
 * one component serve both pages without a mode flag.
 *
 * Renders nothing at all when no provider is configured, so an environment without a Google client
 * id or an Apple Services ID shows the email form exactly as it does today rather than a button
 * that can only fail. **Apple is blank in every environment today** (see `AppleAuthConfig`), so this
 * ships as a Google-only block until the Services ID and the verified domain exist.
 */
export function SocialSignIn(p: {
  /** Namespaces the testids so a spec can tell the Login buttons from the SignUp ones. */
  testIdPrefix: "login" | "signup";
  onGoogleIdToken: (idToken: string) => void;
  onAppleCredential: (credential: AppleCredential) => void;
  /** While the page is mid-request. Google draws its own button, so we can't disable that one —
   *  we make it inert instead, which is the same guarantee from the user's side. */
  disabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isGoogleUnavailable, setIsGoogleUnavailable] = useState(false);
  const [appleError, setAppleError] = useState<string | null>(null);
  const [isAppleBusy, setIsAppleBusy] = useState(false);
  /**
   * Bumped to re-run the mount effect. Without a retry path `isGoogleUnavailable` latches for the
   * life of the page — its effect's only other dep is `showGoogle`, a per-environment constant, and
   * on the SignUp page this component never unmounts. That made the 10s `SCRIPT_LOAD_TIMEOUT_MS` a
   * one-way door: a slow connection times out, the script then arrives and populates `window.google`
   * anyway, and the button stays gone until a manual reload. It also defeated the deliberate choice
   * in `loadProviderScript` to FORGET a failed load so a blip can't poison the provider — nothing
   * was retrying, so the blip poisoned it regardless.
   */
  const [mountAttempt, setMountAttempt] = useState(0);

  const showGoogle = isGoogleConfigured();
  const showApple = isAppleConfigured();

  /**
   * Latest-callback ref so the mount effect can have an empty dep list. Keying the effect on the
   * handler would tear down and redraw Google's button on every parent render.
   */
  const onIdTokenRef = useRef(p.onGoogleIdToken);
  onIdTokenRef.current = p.onGoogleIdToken;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !showGoogle) return;

    let isCancelled = false;
    mountGoogleButton({
      container,
      onIdToken: (idToken) => onIdTokenRef.current(idToken),
    }).catch(() => {
      // A provider that won't load is not a dead end — the email-code path is still right above.
      if (!isCancelled) setIsGoogleUnavailable(true);
    });

    return () => {
      isCancelled = true;
      unmountGoogleButton(container);
    };
  }, [showGoogle, mountAttempt]);

  function onRetryGoogle() {
    setIsGoogleUnavailable(false);
    setMountAttempt((n) => n + 1);
  }

  /**
   * Apple's flow is caller-driven (see `getAppleIdToken`), so unlike Google the click lives here.
   * A cancel resolves `null` and must leave no trace — same rule as Google's silent dismissal.
   */
  async function onApplePress() {
    setAppleError(null);
    setIsAppleBusy(true);
    try {
      const credential = await getAppleIdToken();
      if (credential) p.onAppleCredential(credential);
    } catch {
      setAppleError("We couldn't reach Apple sign-in. Try again, or use your email address above.");
    } finally {
      setIsAppleBusy(false);
    }
  }

  if (!showGoogle && !showApple) return null;

  return (
    <div className="social" data-testid={`${p.testIdPrefix}-social`}>
      <div className="social-divider">
        <span>or</span>
      </div>

      {showGoogle ? (
        isGoogleUnavailable ? (
          <>
            <p className="error" role="alert" data-testid={`${p.testIdPrefix}-social-error`}>
              We couldn't load Google sign-in. Check your connection, or use your email address above.
            </p>
            <button
              type="button"
              className="link"
              data-testid={`${p.testIdPrefix}-social-retry`}
              onClick={onRetryGoogle}
              disabled={p.disabled}
            >
              Try again
            </button>
          </>
        ) : (
          <div
            ref={containerRef}
            data-testid={`${p.testIdPrefix}-google-button`}
            className="social-button"
            style={p.disabled ? { pointerEvents: "none", opacity: 0.6 } : undefined}
          />
        )
      ) : null}

      {showApple ? (
        <>
          <button
            type="button"
            className="apple-button"
            data-testid={`${p.testIdPrefix}-apple-button`}
            onClick={onApplePress}
            disabled={p.disabled || isAppleBusy}
          >
            {/* Apple's mark is required on the button by their Human Interface Guidelines, and we
                draw it ourselves because the promise API leaves the button to us. */}
            <svg className="apple-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M16.365 1.43c0 1.14-.468 2.24-1.223 3.05-.81.87-2.13 1.54-3.23 1.45-.13-1.1.42-2.27 1.15-3.02.81-.86 2.22-1.49 3.3-1.48zM20.9 17.02c-.55 1.27-.81 1.84-1.52 2.96-.99 1.57-2.38 3.52-4.11 3.53-1.53.02-1.93-1-4.01-.99-2.08.01-2.52 1.01-4.06.99-1.72-.02-3.04-1.78-4.03-3.34C.4 15.7-.13 10.53 1.66 7.86 2.93 5.97 4.94 4.86 6.83 4.86c1.93 0 3.14 1.06 4.74 1.06 1.55 0 2.49-1.06 4.72-1.06 1.69 0 3.48.92 4.76 2.51-4.18 2.29-3.5 8.25.85 9.65z"
              />
            </svg>
            {isAppleBusy ? "Waiting for Apple…" : "Continue with Apple"}
          </button>
          {appleError ? (
            <p className="error" role="alert" data-testid={`${p.testIdPrefix}-apple-error`}>
              {appleError}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
